import React, { Suspense, useMemo, useState } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls, Text, Html, Grid, Environment } from "@react-three/drei";
import * as THREE from "three";
import { useEffect } from "react";
import { api } from "@/lib/api";
import { Panel, ModuleHeader } from "@/components/kcc/Panel";
import { StatusChip } from "@/components/kcc/Kpi";
import { X, Loader2, Wrench, AlertTriangle, Sparkles, Layers, Truck, Factory, Waves, Building2, Eye } from "lucide-react";

/* ============================================================
 * Real Three.js Enterprise Digital Twin.
 * Same route (/twin), same permissions, upgraded rendering only.
 * ==========================================================*/

const STATUS_COLOR = {
    running: "#22c55e", idle: "#a5acb8", maintenance: "#f59e0b", down: "#ef4444",
    reduced_rate: "#f59e0b",
    Green: "#22c55e", Amber: "#f59e0b", Red: "#ef4444",
    Alert: "#ef4444", Watch: "#f59e0b", Normal: "#22c55e",
};

const LAYERS = [
    { id: "pit", label: "Open Pit + Fleet", color: "#b87333" },
    { id: "plant", label: "Concentrator & Refinery", color: "#2d68c4" },
    { id: "tsf", label: "Tailings TSF-01 / 02 / 03", color: "#ef4444" },
    { id: "utilities", label: "Utilities · Substation · Fuel", color: "#0ea5e9" },
    { id: "buildings", label: "Workshops · Warehouses · Offices", color: "#a58af6" },
    { id: "roads", label: "Haul Roads", color: "#f59e0b" },
    { id: "sensors", label: "Environmental Sensors", color: "#22c55e" },
];

/* --- deterministic 2D positions for assets --- */
function seededXZ(id, xMin, xMax, zMin, zMax) {
    let h = 0;
    for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
    const rx = ((h % 1000) / 1000);
    const rz = (((h >> 10) % 1000) / 1000);
    return [xMin + rx * (xMax - xMin), 0.2, zMin + rz * (zMax - zMin)];
}

/* --- primitive geometry building blocks --- */
function Ground() {
    return (
        <>
            <mesh receiveShadow rotation-x={-Math.PI / 2} position={[0, -0.05, 0]}>
                <planeGeometry args={[120, 120]} />
                <meshStandardMaterial color="#0f141c" roughness={0.95} metalness={0.02} />
            </mesh>
            <Grid args={[120, 120]} position={[0, 0, 0]} cellSize={2} cellThickness={0.4} cellColor="#1e2434" sectionSize={10} sectionThickness={0.8} sectionColor="#2d3549" fadeDistance={80} fadeStrength={1} infiniteGrid={false} />
        </>
    );
}

const BENCH_RINGS = [
    { id: "bench-0", y: 0 },
    { id: "bench-1", y: -0.7 },
    { id: "bench-2", y: -1.4 },
    { id: "bench-3", y: -2.1 },
    { id: "bench-4", y: -2.8 },
];

function OpenPit({ visible }) {
    if (!visible) return null;
    // Stepped bench rings using tori
    return (
        <group position={[-32, 0, -5]}>
            {BENCH_RINGS.map((r, i) => (
                <mesh key={r.id} position={[0, r.y, 0]} rotation-x={-Math.PI / 2}>
                    <torusGeometry args={[16 - i * 2.5, 0.9, 8, 40]} />
                    <meshStandardMaterial color="#3a2a17" emissive="#b87333" emissiveIntensity={0.05} roughness={0.9} />
                </mesh>
            ))}
            <Text position={[0, 4, 0]} fontSize={2.2} color="#e88c4a" anchorX="center">KOV OPEN PIT</Text>
        </group>
    );
}

function Concentrator({ visible }) {
    if (!visible) return null;
    // Refinery/plant complex — labelled blocks
    const blocks = [
        { name: "CRUSH", pos: [-6, 1.5, 0], size: [4, 3, 4], color: "#1a2540" },
        { name: "MILL", pos: [0, 2.5, 0], size: [4, 5, 4], color: "#1a2540" },
        { name: "FLOTATION", pos: [6, 1.5, 0], size: [5, 3, 4], color: "#182036" },
        { name: "SX-EW", pos: [-3, 1, 6], size: [6, 2, 5], color: "#182036" },
        { name: "REFINERY", pos: [5, 1, 6], size: [7, 2, 5], color: "#182036" },
    ];
    return (
        <group position={[10, 0, 5]}>
            {blocks.map((b) => (
                <group key={b.name} position={b.pos}>
                    <mesh castShadow>
                        <boxGeometry args={b.size} />
                        <meshStandardMaterial color={b.color} emissive="#2d68c4" emissiveIntensity={0.06} metalness={0.4} roughness={0.6} />
                    </mesh>
                    <Text position={[0, b.size[1] / 2 + 0.6, 0]} fontSize={0.7} color="#4f8bd9" anchorX="center">{b.name}</Text>
                </group>
            ))}
            <Text position={[2, 8, 0]} fontSize={1.6} color="#4f8bd9" anchorX="center">CONCENTRATOR &amp; REFINERY</Text>
        </group>
    );
}

function TailingsFacilities({ visible }) {
    if (!visible) return null;
    const tsfs = [
        { name: "TSF-01", pos: [30, 0.1, 12] },
        { name: "TSF-02", pos: [36, 0.1, 16] },
        { name: "TSF-03", pos: [42, 0.1, 12] },
    ];
    return (
        <group>
            {tsfs.map((t) => (
                <group key={t.name} position={t.pos}>
                    <mesh rotation-x={-Math.PI / 2}>
                        <circleGeometry args={[4, 32]} />
                        <meshStandardMaterial color="#3a1717" emissive="#ef4444" emissiveIntensity={0.1} roughness={0.9} />
                    </mesh>
                    <mesh position={[0, 0.05, 0]} rotation-x={-Math.PI / 2}>
                        <ringGeometry args={[3.6, 4, 32]} />
                        <meshStandardMaterial color="#ef4444" emissive="#ef4444" emissiveIntensity={0.2} />
                    </mesh>
                    <Text position={[0, 2.6, 0]} fontSize={0.7} color="#f87171" anchorX="center">{t.name}</Text>
                </group>
            ))}
        </group>
    );
}

function Utilities({ visible }) {
    if (!visible) return null;
    const posts = ["ss4-p1", "ss4-p2", "ss4-p3", "ss4-p4"];
    const tanks = ["fuel-1", "fuel-2", "fuel-3"];
    return (
        <group position={[-6, 0, 22]}>
            {posts.map((id, i) => (
                <mesh key={id} position={[i * 2, 1.5, 0]} castShadow>
                    <boxGeometry args={[0.4, 3, 0.4]} />
                    <meshStandardMaterial color="#0ea5e9" emissive="#0ea5e9" emissiveIntensity={0.25} />
                </mesh>
            ))}
            <mesh position={[3, 1.6, 0]}>
                <boxGeometry args={[7, 0.05, 0.8]} />
                <meshStandardMaterial color="#0ea5e9" />
            </mesh>
            <Text position={[3, 4, 0]} fontSize={0.6} color="#7dd3fc" anchorX="center">SUBSTATION SS4</Text>
            {tanks.map((id, i) => (
                <mesh key={id} position={[10 + i * 2.4, 1.2, 0]} castShadow>
                    <cylinderGeometry args={[1, 1, 2.4, 20]} />
                    <meshStandardMaterial color="#182036" emissive="#0ea5e9" emissiveIntensity={0.08} metalness={0.5} roughness={0.5} />
                </mesh>
            ))}
            <Text position={[12.4, 3, 0]} fontSize={0.55} color="#7dd3fc" anchorX="center">FUEL FARM</Text>
        </group>
    );
}

function Buildings({ visible }) {
    if (!visible) return null;
    const b = [
        { name: "WORKSHOP", pos: [-24, 0.7, 20], size: [5, 1.4, 4] },
        { name: "WAREHOUSE", pos: [-17, 0.8, 20], size: [6, 1.6, 4] },
        { name: "LAB", pos: [-11, 0.7, 20], size: [4, 1.4, 4] },
        { name: "ADMIN", pos: [-24, 0.8, 26], size: [5, 1.6, 4] },
        { name: "SECURITY", pos: [-17, 0.6, 26], size: [3, 1.2, 3] },
    ];
    return (
        <group>
            {b.map((x) => (
                <group key={x.name} position={x.pos}>
                    <mesh castShadow>
                        <boxGeometry args={x.size} />
                        <meshStandardMaterial color="#161d2c" emissive="#a58af6" emissiveIntensity={0.06} />
                    </mesh>
                    <Text position={[0, x.size[1] / 2 + 0.5, 0]} fontSize={0.5} color="#c4b5fd" anchorX="center">{x.name}</Text>
                </group>
            ))}
        </group>
    );
}

function Roads({ visible }) {
    if (!visible) return null;
    // Simple curved roads represented as thin boxes
    const segs = [
        { id: "road-a", pos: [-15, 0.01, 0], size: [30, 0.02, 1.2] },
        { id: "road-b", pos: [10, 0.01, 15], size: [24, 0.02, 1.2] },
        { id: "road-c", pos: [-20, 0.01, 20], size: [1.2, 0.02, 30] },
        { id: "road-d", pos: [24, 0.01, 8], size: [1.2, 0.02, 20] },
    ];
    return (
        <group>
            {segs.map((s) => (
                <mesh key={s.id} position={s.pos}>
                    <boxGeometry args={s.size} />
                    <meshStandardMaterial color="#f59e0b" emissive="#f59e0b" emissiveIntensity={0.05} transparent opacity={0.35} />
                </mesh>
            ))}
        </group>
    );
}

function EnvironmentalSensors({ visible }) {
    if (!visible) return null;
    const pts = [
        { id: "env-nw", pos: [-40, 0.3, -20] },
        { id: "env-n", pos: [10, 0.3, -25] },
        { id: "env-ne", pos: [40, 0.3, -18] },
        { id: "env-e", pos: [45, 0.3, 25] },
        { id: "env-sw", pos: [-40, 0.3, 30] },
        { id: "env-s", pos: [0, 0.3, 35] },
    ];
    return (
        <group>
            {pts.map((p) => (
                <group key={p.id} position={p.pos}>
                    <mesh>
                        <sphereGeometry args={[0.25, 12, 12]} />
                        <meshStandardMaterial color="#22c55e" emissive="#22c55e" emissiveIntensity={0.7} />
                    </mesh>
                    <mesh>
                        <ringGeometry args={[0.55, 0.6, 20]} />
                        <meshBasicMaterial color="#22c55e" transparent opacity={0.35} />
                    </mesh>
                </group>
            ))}
        </group>
    );
}

/* --- Clickable asset marker (billboard sphere + hover tooltip) --- */
function AssetMarker({ asset, onClick }) {
    const [hover, setHover] = useState(false);
    useFrame((state) => {
        if (asset._pulse) asset._pulse.material.emissiveIntensity = 0.5 + Math.sin(state.clock.elapsedTime * 3) * 0.35;
    });
    const color = STATUS_COLOR[asset.status] || "#a5acb8";
    return (
        <group position={asset.pos}>
            <mesh
                ref={(m) => { asset._pulse = m; }}
                onClick={(e) => { e.stopPropagation(); onClick(asset); }}
                onPointerOver={(e) => { e.stopPropagation(); setHover(true); document.body.style.cursor = "pointer"; }}
                onPointerOut={() => { setHover(false); document.body.style.cursor = "default"; }}
                scale={hover ? 1.6 : 1}
            >
                <sphereGeometry args={[0.6, 16, 16]} />
                <meshStandardMaterial color={color} emissive={color} emissiveIntensity={0.7} />
            </mesh>
            {hover && (
                <Html center distanceFactor={12} style={{ pointerEvents: "none" }}>
                    <div className="px-2 py-1 rounded bg-black/90 border border-white/20 text-white text-[10px] whitespace-nowrap">
                        {asset.label || asset.name || asset.tag || asset.id}
                    </div>
                </Html>
            )}
        </group>
    );
}

function Scene({ data, layers, onOpen }) {
    const markers = useMemo(() => {
        if (!data) return [];
        const out = [];
        if (layers.includes("pit")) {
            (data.assets || []).forEach((a) => {
                const [x, y, z] = seededXZ(a.id, -42, -22, -14, 4);
                out.push({ ...a, pos: [x, 0.8, z] });
            });
        }
        if (layers.includes("plant")) {
            (data.streams || []).forEach((s) => {
                const [x, y, z] = seededXZ(s.id, 6, 20, 2, 14);
                out.push({ ...s, pos: [x, 2.4, z] });
            });
        }
        if (layers.includes("tsf")) {
            (data.tailings || []).forEach((t, i) => {
                out.push({ id: t.id, label: `${t.code} · ${t.name}`, status: t.tarp_status, type: "tsf", location: t.name, pos: [30 + i * 6, 0.9, 12 + (i % 2) * 4] });
            });
        }
        return out;
    }, [data, layers]);

    return (
        <>
            <ambientLight intensity={0.35} />
            <directionalLight position={[20, 30, 10]} intensity={0.9} castShadow />
            <directionalLight position={[-15, 20, -10]} intensity={0.35} color="#8bb0ff" />
            <Ground />
            <OpenPit visible={layers.includes("pit")} />
            <Concentrator visible={layers.includes("plant")} />
            <TailingsFacilities visible={layers.includes("tsf")} />
            <Utilities visible={layers.includes("utilities")} />
            <Buildings visible={layers.includes("buildings")} />
            <Roads visible={layers.includes("roads")} />
            <EnvironmentalSensors visible={layers.includes("sensors")} />
            {markers.map((a) => <AssetMarker key={a.id} asset={a} onClick={onOpen} />)}
            <OrbitControls enablePan enableRotate enableZoom minDistance={20} maxDistance={140} maxPolarAngle={Math.PI / 2.2} target={[0, 0, 5]} />
        </>
    );
}

/* --- The Digital Twin page itself --- */
export default function DigitalTwin360() {
    const [data, setData] = useState(null);
    const [layers, setLayers] = useState(LAYERS.map((l) => l.id));
    const [selected, setSelected] = useState(null);
    const [detail, setDetail] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);

    useEffect(() => { api.get("/twin/state").then(({ data }) => setData(data)); }, []);

    async function openAsset(a) {
        setSelected(a);
        setDetail(null);
        setLoadingDetail(true);
        try {
            const { data } = await api.get(`/twin/asset/${a.id}`);
            setDetail(data);
        } catch (_) {
            setDetail(null);
        } finally {
            setLoadingDetail(false);
        }
    }

    function toggleLayer(id) { setLayers((prev) => (prev.includes(id) ? prev.filter((l) => l !== id) : [...prev, id])); }

    const totalMarkers = (data?.assets?.length || 0) + (data?.streams?.length || 0) + (data?.tailings?.length || 0);

    return (
        <div className="space-y-6">
            <ModuleHeader
                kicker="Digital Twin 2.0 · Enterprise 3D"
                title="Kamoto Site Digital Twin"
                description="Real 3D enterprise digital twin — open pit, concentrator, refinery, tailings, utilities, workshops, warehouses and haul roads. Click any asset for its full digital passport, work orders and AI recommendations."
            />

            <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">
                <Panel title="Layers" accent="purple">
                    <div className="space-y-2">
                        {LAYERS.map((l) => {
                            const on = layers.includes(l.id);
                            return (
                                <button
                                    key={l.id}
                                    onClick={() => toggleLayer(l.id)}
                                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-md border transition-colors text-sm ${on ? "border-white/25 bg-white/[0.04] text-white" : "border-white/10 text-kcc-silver hover:text-white"}`}
                                    data-testid={`twin-layer-${l.id}`}
                                >
                                    <span className="w-2 h-2 rounded-full" style={{ background: l.color, boxShadow: `0 0 8px ${l.color}` }} />
                                    <Layers className="w-3.5 h-3.5" />
                                    <span className="flex-1 text-left">{l.label}</span>
                                    <span className={`text-[10px] ${on ? "text-kcc-copper-light" : "kcc-text-dim"}`}>{on ? "ON" : "OFF"}</span>
                                </button>
                            );
                        })}
                    </div>
                    <div className="mt-6 space-y-1.5 text-xs">
                        <div className="kcc-label">Status legend</div>
                        <div className="flex items-center gap-2 mt-2"><span className="w-2 h-2 rounded-full bg-kcc-green" />Running / Normal</div>
                        <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-kcc-amber" />Watch / Reduced</div>
                        <div className="flex items-center gap-2"><span className="w-2 h-2 rounded-full bg-kcc-red" />Down / Critical</div>
                    </div>
                    <div className="mt-6 text-[11px] kcc-text-dim leading-relaxed">
                        Rotate with drag · Pan with right-click drag · Zoom with scroll. Every marker is a live query into the KCC360 data platform.
                    </div>
                </Panel>

                <div className="xl:col-span-3 kcc-panel p-0 overflow-hidden">
                    <div className="p-4 border-b border-white/5 flex items-center justify-between">
                        <div>
                            <div className="kcc-label text-kcc-copper-light">3D Enterprise Twin · live overlay</div>
                            <div className="font-display text-lg font-semibold text-white">Kamoto — Kov, T17, Concentrator, Refinery & TSFs</div>
                        </div>
                        <div className="flex items-center gap-3 text-[11px] kcc-text-silver">
                            <Eye className="w-3.5 h-3.5" /> {totalMarkers} clickable assets
                            <StatusChip status="normal">Live</StatusChip>
                        </div>
                    </div>

                    <div className="kcc-twin-canvas relative" style={{ height: 620 }} data-testid="twin-canvas">
                        <Canvas
                            shadows
                            camera={{ position: [-10, 40, 60], fov: 45 }}
                            gl={{ antialias: true, powerPreference: "high-performance" }}
                            dpr={[1, 1.5]}
                        >
                            <color attach="background" args={["#0b0e14"]} />
                            <fog attach="fog" args={["#0b0e14", 90, 180]} />
                            <Suspense fallback={<Html center><Loader2 className="w-6 h-6 animate-spin text-kcc-copper" /></Html>}>
                                <Scene data={data} layers={layers} onOpen={openAsset} />
                            </Suspense>
                        </Canvas>
                        {!data && (
                            <div className="absolute inset-0 flex items-center justify-center kcc-text-silver text-sm">
                                <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading twin state…
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {selected && (
                <div className="fixed inset-0 z-50 flex justify-end animate-fade-in" data-testid="twin-drawer">
                    <div className="absolute inset-0 bg-black/60" onClick={() => setSelected(null)} />
                    <div className="relative kcc-glass w-full max-w-lg h-full overflow-y-auto p-6 border-l border-white/10 shadow-2xl">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <div className="kcc-label text-kcc-copper-light">Asset Passport</div>
                                <div className="font-display text-2xl font-semibold text-white mt-1">{selected.label || selected.name || selected.tag || selected.id}</div>
                                <div className="text-[11px] kcc-text-silver mt-1">{selected.location || selected.area || "—"}</div>
                            </div>
                            <button onClick={() => setSelected(null)} className="text-kcc-silver hover:text-white" data-testid="twin-drawer-close"><X className="w-5 h-5" /></button>
                        </div>

                        {loadingDetail && <div className="mt-8 flex items-center gap-2 kcc-text-silver"><Loader2 className="w-4 h-4 animate-spin" /> Loading digital passport…</div>}

                        {detail && (
                            <div className="mt-6 space-y-5">
                                <div className="grid grid-cols-2 gap-3 text-xs">
                                    {Object.entries(detail.asset).filter(([k]) => !["id", "is_demo"].includes(k)).slice(0, 14).map(([k, v]) => (
                                        <div key={k} className="p-2.5 rounded border border-white/5 bg-white/[0.02]">
                                            <div className="kcc-label truncate">{k.replace(/_/g, " ")}</div>
                                            <div className="text-white mt-0.5 truncate font-mono-kcc text-[11px]">{typeof v === "object" ? JSON.stringify(v).slice(0, 40) : String(v)}</div>
                                        </div>
                                    ))}
                                </div>
                                {!!detail.work_orders?.length && (
                                    <div>
                                        <div className="kcc-label flex items-center gap-2 mb-2"><Wrench className="w-3.5 h-3.5 text-kcc-copper-light" /> Recent work orders</div>
                                        <div className="space-y-1.5">
                                            {detail.work_orders.map((w) => (
                                                <div key={w.id} className="p-2.5 rounded border border-white/5 flex items-center justify-between text-xs">
                                                    <div><span className="font-mono-kcc text-kcc-copper-light">{w.wo_no}</span> · {w.description}</div>
                                                    <StatusChip status={w.status === "Completed" ? "normal" : w.priority === "P1" ? "critical" : "info"}>{w.priority}</StatusChip>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {!!detail.predictions?.length && (
                                    <div>
                                        <div className="kcc-label flex items-center gap-2 mb-2"><AlertTriangle className="w-3.5 h-3.5 text-kcc-amber" /> AI recommendations</div>
                                        {detail.predictions.map((p) => (
                                            <div key={p.id} className="p-3 rounded border border-white/5 bg-kcc-amber/5 text-xs mb-2">
                                                <div className="text-white">{p.predicted_failure_mode}</div>
                                                <div className="kcc-text-silver mt-1">ETA {p.eta_days}d · {(p.probability * 100).toFixed(0)}% prob · confidence {(p.confidence * 100).toFixed(0)}%</div>
                                                <div className="mt-2 text-white">Recommendation: {p.recommendation}</div>
                                                <div className="mt-1 kcc-text-dim">Requires: {p.requires_approval}</div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <div className="pt-2 border-t border-white/5">
                                    <div className="kcc-label flex items-center gap-2"><Sparkles className="w-3.5 h-3.5 text-kcc-purple" /> Governance</div>
                                    <div className="text-[11px] kcc-text-silver mt-2 leading-relaxed">
                                        Every asset action must be authorised by the correct role. AI recommendations are advisory only and require the stated human approval.
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="kcc-panel p-4 flex items-center gap-3"><Truck className="w-5 h-5 text-kcc-copper-light" /><div><div className="kcc-label">Fleet</div><div className="font-display text-2xl">{data?.assets?.length || 0}</div></div></div>
                <div className="kcc-panel p-4 flex items-center gap-3"><Factory className="w-5 h-5 text-kcc-cobalt-light" /><div><div className="kcc-label">Process streams</div><div className="font-display text-2xl">{data?.streams?.length || 0}</div></div></div>
                <div className="kcc-panel p-4 flex items-center gap-3"><Waves className="w-5 h-5 text-kcc-red" /><div><div className="kcc-label">Tailings facilities</div><div className="font-display text-2xl">{data?.tailings?.length || 0}</div></div></div>
                <div className="kcc-panel p-4 flex items-center gap-3"><Building2 className="w-5 h-5 text-kcc-purple" /><div><div className="kcc-label">Site zones</div><div className="font-display text-2xl">12</div></div></div>
            </div>
        </div>
    );
}
